<!-- Mode toggle (optional) -->
@if (showModeToggle()) {
<div class="zag-appearance-editor__mode-toggle" role="tablist">
  <button
    type="button"
    role="tab"
    class="zag-appearance-editor__mode-btn"
    [class.zag-appearance-editor__mode-btn--active]="mode() === 'icon'"
    [attr.aria-selected]="mode() === 'icon'"
    [disabled]="disabled()"
    (click)="setMode('icon')"
  >
    <span zagIcon="palette" class="zag-appearance-editor__mode-icon"></span>
    {{ iconModeLabel() }}
  </button>
  <button
    type="button"
    role="tab"
    class="zag-appearance-editor__mode-btn"
    [class.zag-appearance-editor__mode-btn--active]="mode() === 'image'"
    [attr.aria-selected]="mode() === 'image'"
    [disabled]="disabled()"
    (click)="setMode('image')"
  >
    <span zagIcon="image" class="zag-appearance-editor__mode-icon"></span>
    {{ imageModeLabel() }}
  </button>
</div>
}

<!-- Icon/Color mode content (show when in icon mode, or when toggle hidden and mode is icon) -->
@if (mode() === 'icon') {
<div class="zag-appearance-editor__fields">
  <!-- Icon field -->
  <div class="zag-appearance-editor__field">
    <span id="icon-label" class="zag-appearance-editor__label">{{ iconLabel() }}</span>
    <div class="zag-appearance-editor__picker-panel">
      <div class="zag-appearance-editor__search">
        <span zagIcon="search" class="zag-appearance-editor__search-icon"></span>
        <input
          id="appearance-icon-search"
          type="search"
          class="zag-appearance-editor__search-input"
          [placeholder]="'common.appearanceEditor.searchIcons' | translate"
          [value]="iconSearchQuery()"
          [disabled]="disabled()"
          [attr.aria-labelledby]="'icon-label'"
          [attr.aria-label]="'common.appearanceEditor.searchIcons' | translate"
          autocomplete="off"
          (input)="onIconSearchInput($event)"
          (blur)="onFieldBlur()"
        />
      </div>

      <div class="zag-appearance-editor__picker-content" [attr.aria-labelledby]="'icon-label'">
        @if (iconsService.isLoading()) {
        <div class="zag-appearance-editor__state">
          <span class="zag-appearance-editor__spinner"></span>
          {{ 'common.appearanceEditor.loadingIcons' | translate }}
        </div>
        } @else if (filteredIconGroups().length === 0) {
        <div class="zag-appearance-editor__state">{{ 'common.appearanceEditor.noIconsFound' | translate }}</div>
        } @else { @for (group of filteredIconGroups(); track group.category) {
        <div class="zag-appearance-editor__category">
          <div class="zag-appearance-editor__category-label">{{ group.label }}</div>
          <div class="zag-appearance-editor__icon-grid">
            @for (icon of group.icons; track icon.id) {
            <button
              type="button"
              class="zag-appearance-editor__icon-button"
              [class.zag-appearance-editor__icon-button--selected]="isIconSelected(icon)"
              [disabled]="disabled()"
              [attr.aria-label]="icon.label"
              [attr.aria-pressed]="isIconSelected(icon)"
              [title]="icon.label"
              (click)="selectIcon(icon)"
            >
              <span [zagIcon]="icon.name" class="zag-appearance-editor__icon-choice"></span>
            </button>
            }
          </div>
        </div>
        } }
      </div>
    </div>
  </div>

  <!-- Color field -->
  <div class="zag-appearance-editor__field">
    <span id="color-label" class="zag-appearance-editor__label">{{ colorLabel() }}</span>
    <div class="zag-appearance-editor__picker-panel">
      <div
        class="zag-appearance-editor__picker-content zag-appearance-editor__picker-content--colors"
        [attr.aria-labelledby]="'color-label'"
      >
        <div class="zag-appearance-editor__color-grid">
          @for (color of availableColors(); track color.name) {
          <button
            type="button"
            class="zag-appearance-editor__color-button"
            [class.zag-appearance-editor__color-button--selected]="isColorSelected(color)"
            [style.background-color]="color.value"
            [disabled]="disabled()"
            [attr.aria-label]="color.label"
            [attr.aria-pressed]="isColorSelected(color)"
            [title]="color.label"
            (click)="selectColor(color)"
          >
            @if (isColorSelected(color)) {
            <span class="zag-appearance-editor__check" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
              </svg>
            </span>
            }
          </button>
          }
        </div>
      </div>

      <div class="zag-appearance-editor__custom-color">
        <label for="appearance-custom-color" class="zag-appearance-editor__custom-color-label">
          {{ 'common.appearanceEditor.customColor' | translate }}
        </label>
        <div class="zag-appearance-editor__custom-color-input-wrapper">
          <input
            id="appearance-custom-color"
            type="text"
            class="zag-appearance-editor__custom-color-input"
            placeholder="#000000"
            [value]="customColorInput()"
            [disabled]="disabled()"
            maxlength="7"
            (input)="onCustomColorInput($event)"
            (blur)="onFieldBlur()"
          />
          <button
            type="button"
            class="zag-appearance-editor__custom-color-apply"
            [disabled]="disabled() || !isCustomColorValid()"
            (click)="applyCustomColor()"
          >
            {{ 'common.apply' | translate }}
          </button>
        </div>
        @if (customColorInput() && isCustomColorValid()) {
        <div class="zag-appearance-editor__custom-color-preview" [style.background-color]="customColorInput()"></div>
        }
      </div>
    </div>
  </div>
</div>
}

<!-- Image mode content (show when in image mode, regardless of toggle visibility) -->
@if (mode() === 'image') {
<div class="zag-appearance-editor__image-section">
  <!-- Image URL input (optional) -->
  @if (showImageUrlInput()) {
  <div class="zag-appearance-editor__field zag-appearance-editor__field--full">
    <label for="appearance-image-url" class="zag-appearance-editor__label">{{ imageUrlLabel() }}</label>
    <zag-input
      id="appearance-image-url"
      [formControl]="form.controls.imageUrl"
      type="url"
      [placeholder]="imageUrlPlaceholder()"
      (blur)="onFieldBlur()"
    />
  </div>

  <!-- Separator -->
  <div class="zag-appearance-editor__separator">
    <span class="zag-appearance-editor__separator-line"></span>
    <span class="zag-appearance-editor__separator-text">{{ 'common.appearanceEditor.or' | translate }}</span>
    <span class="zag-appearance-editor__separator-line"></span>
  </div>
  }

  <!-- File upload button -->
  <div class="zag-appearance-editor__upload">
    <input
      #fileInput
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
      class="zag-appearance-editor__file-input"
      (change)="onFileSelected($event)"
      [disabled]="disabled()"
    />
    <button
      type="button"
      class="zag-appearance-editor__upload-btn"
      [disabled]="disabled() || isProcessingFile()"
      (click)="triggerFileUpload()"
    >
      <span zagIcon="upload" class="zag-appearance-editor__upload-icon"></span>
      @if (isProcessingFile()) { {{ 'common.processing' | translate }} } @else { {{ uploadLabel() }} }
    </button>
    <span class="zag-appearance-editor__upload-hint">{{ 'common.appearanceEditor.uploadHint' | translate }}</span>
  </div>

  <!-- Upload error -->
  @if (uploadError()) {
  <div class="zag-appearance-editor__error" role="alert">
    <span zagIcon="error" class="zag-appearance-editor__error-icon"></span>
    {{ uploadError() }}
  </div>
  }
</div>
}

<!-- Preview section -->
@switch (previewType()) { @case ('image') {
<div class="zag-appearance-editor__preview">
  <span class="zag-appearance-editor__preview-label">{{ 'common.appearanceEditor.preview' | translate }}</span>
  <div class="zag-appearance-editor__preview-content">
    <img [src]="previewImageUrl()" [alt]="'common.appearanceEditor.preview' | translate" class="zag-appearance-editor__preview-image" />
  </div>
  <button type="button" class="zag-appearance-editor__clear" (click)="clearAll()" [disabled]="disabled()">
    {{ 'common.appearanceEditor.clearAll' | translate }}
  </button>
</div>
} @case ('icon') {
<div class="zag-appearance-editor__preview">
  <span class="zag-appearance-editor__preview-label">{{ 'common.appearanceEditor.preview' | translate }}</span>
  <div
    class="zag-appearance-editor__preview-content zag-icon-surface-gradient"
    [class.zag-appearance-editor__preview-content--fallback]="hasFallbackPreviewBackground()"
    [style.background-color]="previewColor()"
  >
    @if (previewIcon()) {
    <span
      [zagIcon]="previewIcon()!"
      class="zag-appearance-editor__preview-icon"
      [style.--zag-appearance-preview-icon-color]="previewIconColor()"
    ></span>
    }
  </div>
  <button type="button" class="zag-appearance-editor__clear" (click)="clearAll()" [disabled]="disabled()">
    {{ 'common.appearanceEditor.clearAll' | translate }}
  </button>
</div>
} }
